/**
 * Canonical pair-key derivation for arena 1v1 evaluations.
 *
 * Every vote and aggregate doc is keyed by `(mode, profile1, profile2)` where
 * `profile1 < profile2` under lexicographic byte comparison. Swapping A and B
 * in the sidebar therefore hits the same aggregate doc — this is the
 * symmetry invariant surfaced to the user on the Results page.
 *
 * Keep this module dependency-free so both the frontend bundle and the
 * backend Bun runtime can import it without pulling in zod / firebase-admin.
 */

import type { Mode } from "./types";

export function canonicalPair(a: string, b: string): [string, string] {
  return a < b ? [a, b] : [b, a];
}

export function canonicalize(a: string, b: string): { profile1: string; profile2: string } {
  const [profile1, profile2] = canonicalPair(a, b);
  return { profile1, profile2 };
}

export function canonicalKey(
  mode: Mode,
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  if (!a || !b) return null;
  const { profile1, profile2 } = canonicalize(a, b);
  return pairAggregateDocId(mode, profile1, profile2);
}

export function pairAggregateDocId(mode: Mode, profile1: string, profile2: string): string {
  return `${mode}__${profile1}__${profile2}`;
}

/**
 * Matches the profile-name convention used by `scripts/generate_profiles.py`
 * (leading letter, then `[A-Za-z0-9_-]`). Use at every API boundary that
 * accepts client-provided profile names so arbitrary strings cannot flow
 * into Firestore doc IDs or Vespa ranking requests.
 */
const PROFILE_NAME_RE = /^[A-Za-z][A-Za-z0-9_-]*$/;

export function isValidProfileName(name: string): boolean {
  return PROFILE_NAME_RE.test(name);
}

export function assertProfileName(name: string): void {
  if (!isValidProfileName(name)) {
    throw new Error(`invalid profile name: ${JSON.stringify(name)}`);
  }
}
